import { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../config/supabaseClient';
import {
  logLoginSuccess,
  logLoginFailed,
  logLogout,
  logPasswordResetRequest,
  logPasswordChanged,
  logAccountCreated,
  logAccountDeleted
} from '../services/auditService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOrganization, setActiveOrganization] = useState(() => {
    // Load active organization from localStorage on mount
    const stored = localStorage.getItem('activeOrganization');
    return stored ? JSON.parse(stored) : null;
  });

  // Save active organization to localStorage whenever it changes
  useEffect(() => {
    if (activeOrganization) {
      localStorage.setItem('activeOrganization', JSON.stringify(activeOrganization));
    } else {
      localStorage.removeItem('activeOrganization');
    }
  }, [activeOrganization]);

  // Handle authentication errors
  const handleAuthError = (error) => {
    console.error('Authentication error:', error);

    // Handle specific error types
    if (error.message?.includes('refresh_token') || error.message?.includes('JWT')) {
      setError('Your session has expired. Please log in again.');
      setUser(null);
      return 'SESSION_EXPIRED';
    } else if (error.message?.includes('Invalid login credentials')) {
      setError('Invalid email or password. Please try again.');
      return 'INVALID_CREDENTIALS';
    } else if (error.message?.includes('Email not confirmed')) {
      setError('Please verify your email address before logging in.');
      return 'EMAIL_NOT_CONFIRMED';
    } else if (error.message?.includes('Network')) {
      setError('Network error. Please check your connection.');
      return 'NETWORK_ERROR';
    } else {
      setError(error.message || 'An unexpected error occurred.');
      return 'UNKNOWN_ERROR';
    }
  };

  // Clear error state
  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    // Check active sessions and sets the user
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          handleAuthError(sessionError);
          setUser(null);
        } else {
          setUser(session?.user ?? null);
          setError(null);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        handleAuthError(error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {

        // Handle specific auth events
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setError(null);
        } else if (event === 'TOKEN_REFRESHED') {
          setUser(session?.user ?? null);
          setError(null);
        } else if (event === 'SIGNED_IN') {
          setUser(session?.user ?? null);
          setError(null);
        } else if (event === 'USER_UPDATED') {
          setUser(session?.user ?? null);
        }

        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });


      if (error) throw error;

      // Log account creation
      if (data.user) {
        await logAccountCreated(data.user.id, email);
      }

      return { data, error: null };
    } catch (error) {
      console.error('AuthContext: Error signing up:', error);
      await logLoginFailed(email, error.message);
      return { data: null, error };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Log successful login
      if (data.user) {
        await logLoginSuccess(data.user.id, email, 'email');
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error signing in:', error);
      await logLoginFailed(email, error.message);
      return { data: null, error };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Log logout before signing out
      if (user) {
        await logLogout(user.id, user.email);
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear active organization on sign out
      setActiveOrganization(null);

      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { error };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      // Log password reset request
      await logPasswordResetRequest(email);

      return { data, error: null };
    } catch (error) {
      console.error('Error resetting password:', error);
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Log password change
      if (user) {
        await logPasswordChanged(user.id, user.email);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating password:', error);
      return { data: null, error };
    }
  };

  // Sign in with Google OAuth
  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent' // Changed from 'select_account' to 'consent'
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in with Google:', error);
      return { data: null, error };
    }
  };

  // Sign in with Apple OAuth
  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in with Apple:', error);
      return { data: null, error };
    }
  };

  // Delete user account via Edge Function
  const deleteAccount = async () => {
    try {
      if (!user) {
        return { error: { message: 'No user logged in' } };
      }


      // Get the current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error('No active session found');
        return { error: { message: 'No active session. Please log in again.' } };
      }


      // Call Edge Function using direct fetch (more reliable than Supabase SDK)
      const functionUrl = `https://logrwihfeysomnxqhuqb.supabase.co/functions/v1/delete_user`;

      const { data, error } = await supabase.functions.invoke('delete_user', {
        body: { user_id: user.id },
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });


      if (error) {
        console.error('❌ Edge Function Error:', error);
        throw error;
      }


      // Log account deletion
      await logAccountDeleted(user.id, user.email);

      // Sign out after successful deletion
      await signOut();

      return { data, error: null };

    } catch (err) {
      console.error('❌ Unexpected error during account deletion:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);

      return {
        data: null,
        error: {
          message: err.message || 'Failed to delete account. Please try again.'
        }
      };
    }
  };
  

  const value = {
    user,
    loading,
    error,
    clearError,
    activeOrganization,
    setActiveOrganization,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    signInWithGoogle,
    signInWithApple,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
